/* The sleeve. A 1:1 cover image when one exists, otherwise the catalogue number
 * set in the display face bottom-left on a palette field.
 *
 * SoundCloud archive covers are already the published identity of those sets,
 * so they stay visible. Locally created episodes without an image retain the
 * station's typographic sleeve; the fallback is still a finished design rather
 * than a broken-image state.
 *
 * The sleeve is `aria-hidden` by default. Every place the design uses one, the
 * catalogue number and the title are already in text beside it, so announcing
 * the sleeve would read the same number twice.
 */

import type { CSSProperties } from 'react';

import type { ColorToken } from '../types';
import { catalogueDigits } from './shared';

/**
 * Five drawn sizes rather than a free number, because the display face has to
 * be set per size — a 44px sleeve with a 20px catalogue number on it is a
 * smudge, and `clamp()` cannot know it is inside a table row.
 *
 *   xs   44px — the mobile schedule row
 *   sm   56px — an episode row on a resident page
 *   md   64px — the desktop schedule row
 *   card       fills its column, the archive grid
 *   hero       fills its box with the button shadow, the episode and off-air heroes
 */
export type ArtworkSize = 'xs' | 'sm' | 'md' | 'card' | 'hero';

export interface ArtworkProps {
  /** The palette field. Straight from `artwork_color` on the wire. */
  color: ColorToken;
  /** Public cover rendition. Null keeps the typographic fallback. */
  imageUrl?: string | null;
  /**
   * The catalogue number. Fills both faces on its own: the digits go big
   * bottom-left, the whole number goes small top-right.
   */
  catalogueNo?: string | null;
  /** Overrides the big face. A host sleeve passes its server-computed monogram. */
  display?: string | null;
  /** Overrides the small top-right line, e.g. "Staff pick" or "Resident · 006". */
  label?: string | null;
  size?: ArtworkSize;
  /**
   * The sleeve carries information nothing next to it repeats. Rare; the
   * roster grid is the one place, where the monogram is the only mark.
   */
  decorative?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function Artwork({
  color,
  imageUrl,
  catalogueNo,
  display,
  label,
  size = 'card',
  decorative = true,
  className,
  style,
}: ArtworkProps) {
  const big = display ?? catalogueDigits(catalogueNo);
  /* Only the two big sleeves get the catalogue number top-right on their own:
     at 64px and under it is four characters of 9px mono fighting the display
     face for the same square, and the row already prints the number in text. */
  const small = label ?? (size === 'card' || size === 'hero' ? catalogueNo ?? null : null);

  const classes = [
    `art art--${color}`,
    `art--${size}`,
    imageUrl ? 'art--image' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      style={style}
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : [small, big].filter(Boolean).join(' ')}
    >
      {imageUrl ? <img className="art__image" src={imageUrl} alt="" loading="lazy" /> : null}
      {small ? <span className="art__mg">{small}</span> : null}
      {!imageUrl && big ? <span className="art__cat">{big}</span> : null}
    </div>
  );
}
